import type { ZodType } from "zod";

import { HqApiError, NetworkError, toHqError } from "./errors.js";
import { paths } from "./paths.js";
import { currentLocale, translate } from "../../i18n/index.js";

/**
 * Nơi **DUY NHẤT** trong dashboard gọi `fetch`.
 *
 * Cổng ESLint `no-direct-fetch` chặn mọi chỗ khác. Không phải để cho gọn: `credentials: "include"`
 * và **parse phản hồi qua schema** đều là thứ quên một lần là hỏng một màn hình theo cách chỉ lộ ra
 * khi dữ liệu thật khác dữ liệu mẫu.
 *
 * ── Vì sao parse qua schema ở ĐÂY ───────────────────────────────────────────
 *
 * Hub đọc 12 cơ sở dữ liệu nó không sở hữu. Nếu giao diện tin phản hồi mà không kiểm, thì một thay
 * đổi ở API sẽ hiện ra thành `undefined` giữa một bảng số — trông như « chưa có dữ liệu » chứ không
 * như một lỗi. Parse tại cửa biến nó thành một lỗi có tên.
 */
export type RequestOptions<T> = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  schema: ZodType<T>;
  signal?: AbortSignal;
  /** Header thêm — hiện chỉ dùng cho `Idempotency-Key`. */
  headers?: Record<string, string>;
};

/**
 * Gốc của API — HQ-2026-004.
 *
 * Dev: rỗng ⇒ đường tương đối, đi qua proxy của Vite (cùng nguồn gốc). Production: dashboard nằm trên
 * Cloudflare Pages, API trên Cloud Run — KHÁC host, nên phải đóng địa chỉ API vào bundle lúc build. Thiếu
 * biến này thì trang đăng nhập vẫn hiện, còn mọi lượt gọi trả HTML 404 của Pages; workflow deploy chặn
 * trường hợp đó bằng cách tìm chuỗi URL trong bundle.
 *
 * Cookie phiên vẫn đi: `SameSite=Lax` xét theo SITE chứ không theo origin, và hai subdomain cùng một miền
 * là cùng site.
 */
const API_BASE = (import.meta.env.VITE_HQ_API_URL ?? "").replace(/\/+$/, "");

/**
 * Gốc TUYỆT ĐỐI của API — HQ-2026-008, ghép URL webhook phòng thử khi máy chủ chưa khai `HQ_PUBLIC_API_URL`. Dev (gốc
 * rỗng, đi qua proxy Vite) ⇒ origin của dashboard: đúng đường, nhưng đối tác ngoài không gọi tới được máy dev.
 */
export const apiOrigin = (): string => API_BASE || window.location.origin;

/**
 * Ai cần biết khi máy chủ nói « phiên không còn » — `session-store` đăng ký để xoá phiên, và
 * `SessionGate` đưa người dùng về màn đăng nhập.
 *
 * Vì sao ở ĐÂY: `SessionGate` chỉ hỏi `/me` một lần lúc mở trang. Phiên hết hạn GIỮA CHỪNG thì mọi
 * màn hiện « Chưa đăng nhập hoặc phiên đã hết hạn. » mà không ai đưa người dùng đi đâu cả.
 * Đây là nơi duy nhất mọi request đi qua, nên là nơi duy nhất bắt được hết.
 */
let unauthenticatedListener: (() => void) | null = null;
export function onUnauthenticated(listener: () => void): void {
  unauthenticatedListener = listener;
}

/** Đăng nhập sai mật khẩu cũng là `HQ_UNAUTHENTICATED` — nhưng đó là câu trả lời cho form, không phải phiên chết. */
const CREDENTIAL_PATHS = new Set([paths.auth.login(), paths.auth.devLogin()]);

export async function request<T>(path: string, options: RequestOptions<T>): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: options.method ?? "GET",
      // Phiên đi bằng cookie `HttpOnly`. Thiếu dòng này thì đăng nhập « thành công » nhưng mọi
      // request sau đó là 401, và không có thông báo nào nói vì sao.
      credentials: "include",
      headers: {
        // HQ-2026-006 — máy chủ trả câu (lỗi, lý do…) theo ngôn ngữ đang chọn.
        "Accept-Language": currentLocale(),
        ...(options.body === undefined ? {} : { "content-type": "application/json" }),
        ...options.headers,
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (!response.ok) {
    const error = await toHqError(response);
    if (error.code === "HQ_UNAUTHENTICATED" && !CREDENTIAL_PATHS.has(path)) unauthenticatedListener?.();
    throw error;
  }

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);
  const parsed = options.schema.safeParse(payload);
  if (!parsed.success) {
    throw new HqApiError(
      "HQ_INTERNAL",
      translate("common", "contractMismatch", {
        path,
        issue: parsed.error.issues[0]?.message ?? translate("common", "unknownIssue"),
      }),
      response.status,
      "",
    );
  }
  return parsed.data;
}
